/**
 * Legacy single-facet loader.
 *
 * Keep while any old screens still request one facet independently.
 */
export async function fetch(
  {
    state,
    commit,
  },
  {
    facet,
    params,
    api,
    rankingConfig = {},
  },
) {
  if (!facet) {
    throw new Error(
      'facet/fetch requires facet',
    );
  }

  if (
    typeof api !==
    'function'
  ) {
    throw new Error(
      'facet/fetch requires api',
    );
  }

  const current =
    state.items[facet];

  const sequence =
    (
      current?.sequence ||
      0
    ) + 1;

  commit(
    'startRequest',
    {
      facet,
      sequence,
    },
  );

  try {
    const {
      data,
    } =
      await api( params );

    /*
     * A newer request/reset has already replaced this request.
     */
    if (
      state.items[facet]
        ?.sequence !==
      sequence
    ) {
      return;
    }

    const payload =
      data?.facets?.[facet];

    if (!payload) {
      commit(
        'failFacet',
        facet,
      );

      return;
    }

    commit(
      'applyFacet',
      {
        facet,
        payload,
        rankingConfig,
      },
    );
  } catch (error) {
    if (
      state.items[facet]
        ?.sequence !==
      sequence
    ) {
      return;
    }

    commit(
      'failFacet',
      facet,
    );
  }
}

/**
 * Load every configured marketplace facet through one HTTP request.
 *
 * The API response is expected to have:
 *
 * {
 *   facets: {
 *     brands: {...},
 *     category_types: {...}
 *   }
 * }
 *
 * Facets still retain independent Vuex state, loading/error state,
 * ranking configuration and stale-request sequences.
 */
export async function fetchMany(
  {
    state,
    commit,
  },
  {
    facets = [],
    params = {},
    configs = {},
    api,
  },
) {
  if (
    !Array.isArray( facets ) ||
    facets.length === 0
  ) {
    return;
  }

  if (
    typeof api !==
    'function'
  ) {
    throw new Error(
      'facet/fetchMany requires api',
    );
  }

  const sequences = {};

  /*
   * Start one logical request for each facet even though all
   * facets share one physical HTTP request.
   *
   * This lets resetFacet/resetAll/new requests invalidate stale
   * responses independently.
   */
  facets.forEach(
    ( facet ) => {
      const current =
        state.items[facet];

      const sequence =
        (
          current?.sequence ||
          0
        ) + 1;

      sequences[facet] =
        sequence;

      commit(
        'startRequest',
        {
          facet,
          sequence,
        },
      );
    },
  );

  try {
    const {
      data,
    } =
      await api( params );

    const responseFacets =
      data?.facets ||
      {};

    facets.forEach(
      ( facet ) => {
        /*
         * Ignore a stale response for this facet.
         */
        if (
          state.items[facet]
            ?.sequence !==
          sequences[facet]
        ) {
          return;
        }

        const config =
          configs[facet];

        if (!config) {
          commit(
            'failFacet',
            facet,
          );

          return;
        }

        const payload =
          responseFacets[facet];

        /*
         * Backend response can theoretically omit one facet.
         * Mark only that facet failed rather than throwing away
         * other valid facets in the same response.
         */
        if (!payload) {
          commit(
            'failFacet',
            facet,
          );

          return;
        }

        commit(
          'applyFacet',
          {
            facet,
            payload,

            rankingConfig: {
              scope:
              config.scope,

              getId: ( row ) =>
                row?.[
                  config.valueKey
                  ],

              getLabel: ( row ) =>
                row?.[
                  config.labelKey
                  ],

              getCount: ( row ) =>
                row?.count,

              getStatus: ( row ) =>
                row?.status,

              fuzzy:
                config.fuzzy ??
                true,

              requireSameFirstCharacter:
                config
                  .requireSameFirstCharacter ??
                true,
            },
          },
        );
      },
    );
  } catch (error) {
    facets.forEach(
      ( facet ) => {
        /*
         * A reset/new fetch may have invalidated this request
         * while the HTTP call was in progress.
         */
        if (
          state.items[facet]
            ?.sequence !==
          sequences[facet]
        ) {
          return;
        }

        commit(
          'failFacet',
          facet,
        );
      },
    );

    /*
     * Intentionally don't throw.
     *
     * Facet errors are represented through Vuex state and the
     * SearchableFacet retry UI, matching the legacy fetch action.
     */
  }
}

export function resetFacet(
  {
    commit,
  },
  facet,
) {
  commit(
    'resetFacet',
    facet,
  );
}

export function resetAll(
  {
    commit,
  },
) {
  commit(
    'resetAll',
  );
}
